import axios from "axios";
import { backendUrl } from "./Router/constant";
import {
  clearAuthenticationStorage,
  getAuthToken,
  normalizeAuthToken,
} from "./utils/authStorage";

const axionIns = axios.create({
  baseURL: backendUrl + "/",
  headers: {
    "Content-Type": "application/json",
  },
});
let authRedirecting = false;

const getTokenFailureMessage = (data) =>
  String(
    data?.message ||
      data?.data?.message ||
      data?.data?.data?.message ||
      data?.error ||
      data?.data?.error ||
      "",
  );

const confirmsTokenFailure = (message) =>
  /(?:invalid|expired|missing|required|revoked).{0,32}(?:token|jwt|session)|(?:token|jwt|session).{0,32}(?:invalid|expired|missing|required|revoked)|jwt expired/i.test(
    String(message || ""),
  );

const verifyVendorToken = async (token) => {
  const url = `${backendUrl}/vendors/verify-token`;
  const config = {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 10000,
  };
  try {
    const response = await axios.get(url, config);
    const body = response?.data?.data || response?.data || {};
    const explicitlyInvalid =
      body?.valid === false ||
      body?.verified === false ||
      body?.isValid === false ||
      body?.authenticated === false ||
      (body?.success === false && confirmsTokenFailure(getTokenFailureMessage(body)));
    if (response?.status >= 200 && response?.status < 300) {
      return !explicitlyInvalid;
    }
    return response?.status === 401 || explicitlyInvalid || null;
  } catch (error) {
    const status = error?.response?.status;
    const body = error?.response?.data || {};
    const explicitlyInvalid =
      body?.valid === false ||
      body?.verified === false ||
      body?.isValid === false ||
      body?.authenticated === false ||
      confirmsTokenFailure(getTokenFailureMessage(body));
    if (status === 401 || (status >= 400 && status < 500 && explicitlyInvalid)) return false;
    return null;
  }
};

const clearInvalidSession = () => {
  clearAuthenticationStorage();
  if (window.location.pathname !== "/" && !authRedirecting) {
    authRedirecting = true;
    window.location.replace("/");
  }
};

axionIns.interceptors.request.use((config) => {
  const configuredAuthorization =
    config.headers?.get?.("Authorization") ||
    config.headers?.Authorization ||
    config.headers?.authorization;
  const token = configuredAuthorization
    ? normalizeAuthToken(configuredAuthorization)
    : getAuthToken();
  config.__authToken = token || "";
  config.__authTokenPresent = Boolean(token);
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    // Let the browser add the multipart boundary. Keeping the JSON default
    // here makes DevTools show the file as an object instead of Form Data.
    if (typeof config.headers?.delete === "function") {
      config.headers.delete("Content-Type");
    } else if (config.headers) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }
  }
  if (token && !configuredAuthorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axionIns.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const responseBody = error?.response?.data || {};
    const message = getTokenFailureMessage(responseBody);
    const requestUrl = String(error?.config?.url || "").split(/[?#]/, 1)[0];
    const isAuthSubmission =
      /(?:^|\/)(vendors|clients|admin)\/(signin|signup)(?:\/|$)|\/(forgot-password|reset-password)(?:\/|$)|(?:^|\/)verify-otp(?:\/|$)/i.test(
        requestUrl,
      );
    const isVerifyRequest = /(?:^|\/)(vendors|clients|admin)\/verify-token(?:\/|$)/i.test(
      requestUrl,
    );
    const requestToken = error?.config?.__authToken;
    const sameSession =
      requestToken && getAuthToken() === requestToken;
    const isTokenRejected =
      (status === 401 && isVerifyRequest) ||
      (status >= 400 && status < 500 && confirmsTokenFailure(message));

    if (
      sameSession &&
      error?.config?.__authTokenPresent &&
      !isAuthSubmission
    ) {
      let invalidToken = isVerifyRequest && isTokenRejected;
      if (!isVerifyRequest && (status === 401 || isTokenRejected)) {
        invalidToken = (await verifyVendorToken(requestToken)) === false;
      }
      if (
        invalidToken &&
        getAuthToken() === requestToken
      ) {
        clearInvalidSession();
      }
    }

    return Promise.reject(error);
  },
);

export default axionIns;